import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { PermissionLevelValue } from '@backModels/Ref.PermissionLevelValue';
import { AlertMessageService } from '@shared/services/alert-message.service';
import { firstValueFrom, skipWhile, timeout } from 'rxjs';
import { AccessVerifyRouteData } from 'src/app/models/AccessVerifyRouteData';
import { AuthService } from '../services/auth.service';
import { CurrentUserService } from '../services/current-user.service';

/**
 * Función auxiliar para buscar el parámetro campaignGuid en la jerarquía de rutas
 */
export function getCampaignGuidFromRoute(route: ActivatedRouteSnapshot): string | null {
  let currentRoute: ActivatedRouteSnapshot | null = route;

  // Recorrer hacia arriba en la jerarquía de rutas
  while (currentRoute) {
    const campaignGuid = currentRoute.paramMap.get('campaignGuid');
    if (campaignGuid) {
      return campaignGuid;
    }
    currentRoute = currentRoute.parent;
  }

  return null;
}

export const AccessVerifierGuard: CanActivateFn = async (route: ActivatedRouteSnapshot /*, _state: RouterStateSnapshot*/) => {
  const currentUserService = inject(CurrentUserService); // Injecting the CurrentUserService
  const router = inject(Router); // Injecting the Router for navigation
  const authService = inject(AuthService); // Injecting the AuthService for authentication
  const alertMessageService = inject(AlertMessageService); // Injecting AlertMessageService for error handling

  const AUTH_TIMEOUT = 10000; // 10 segundos de timeout

  try {
    // Si estamos en medio del callback de MSAL (URL contiene #code o #error), esperar brevemente a que MSAL termine
    if (window.location.hash.includes('code=') || window.location.hash.includes('error=')) {
      // Esperar mientras MSAL esté procesando (máximo 3 segundos)
      const maxWait = 3000;
      const startTime = Date.now();
      while (authService.isRedirecting) {
        if (Date.now() - startTime > maxWait) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Esperar un poco más para que LOGIN_SUCCESS complete la navegación (máximo 2 segundos adicionales)
      const maxWaitForNavigation = 2000;
      const navStartTime = Date.now();
      while (window.location.hash.includes('code=') || window.location.hash.includes('error=')) {
        if (Date.now() - navStartTime > maxWaitForNavigation) {
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Permitir que la navegación de LOGIN_SUCCESS tome control
      return true;
    }

    let isLoggedIn = false;

    // Espera si MSAL está procesando el redirect
    if (authService.isRedirecting) {
      await new Promise(resolve => setTimeout(resolve, 500)); // espera breve
    }

    const authType = localStorage.getItem('auth_type');

    if (authType === 'jwt') {
      // Usuario JWT externo - el token está en una cookie HttpOnly
      // El backend verificará la cookie automáticamente
      isLoggedIn = true;
    } else {
      // Para usuarios Azure AD (o sin auth_type definido, asumimos Azure AD por defecto)
      // Sincroniza el estado de login con MSAL
      await authService.initializeMsal();

      // Verifica si ya hay una cuenta activa en MSAL
      await authService.checkAccount();
      if (authService.isLoggedIn) {
        isLoggedIn = true;
      } else {
        try {
          // Espera el stream solo si no hay sesión activa
          isLoggedIn = await firstValueFrom(
            authService.isLoggedInStream.pipe(
              skipWhile(value => value === null || value === undefined),
              timeout(AUTH_TIMEOUT),
            ),
          );
        } catch {
          // Timeout - no hay sesión
          isLoggedIn = false;
        }
      }
    }

    if (!isLoggedIn) {
      // Si es usuario Azure AD sin autenticar, iniciar login preservando la URL
      if (authType !== 'jwt') {
        // Solo iniciar login si NO estamos ya en proceso de redirect
        const isRedirecting = localStorage.getItem('azure_login_redirecting');
        if (!isRedirecting) {
          // Usar window.location.pathname que tiene la URL completa que el usuario intentó acceder
          const targetUrl = window.location.pathname + window.location.search;
          localStorage.setItem('azure_login_redirecting', 'true');
          authService.login(targetUrl);
        }
        return false;
      }
      // Para usuarios JWT sin autenticar, redirigir a 401
      await router.navigate(['error/401']);
      return false;
    }

    const currentUser = await currentUserService.get();

    if (currentUser?.user?.userGuid && route.data) {
      const routeData = route.data as AccessVerifyRouteData;

      // Extraer campaignGuid del parámetro de la ruta
      //const campaignGuid = route.paramMap.get('campaignGuid');
      const campaignGuid = getCampaignGuidFromRoute(route);
      let hasPermission = false;

      const isExternal = localStorage.getItem('auth_type') === 'jwt';
      if (campaignGuid) {
        if (isExternal) {
          // For external users, backend enforces NIF-based access. If logged-in with JWT, allow route; backend will 403 if not owner.
          hasPermission = true;
        } else {
          // Cache de permisos indexa GUIDs en lowercase; URLs externas (correos, queries
          // SQL con CAST sin LOWER, etc.) pueden llegar en mayúsculas y producir un 403
          // espurio. Normalizamos el lookup para que el casing nunca afecte al acceso.
          hasPermission = currentUserService.hasPermissionOnEntity(routeData.featureId, PermissionLevelValue.Read, campaignGuid.toLowerCase());
        }
      } else {
        if (isExternal) {
          hasPermission = true;
        } else {
          hasPermission = currentUserService.hasPermission([routeData.featureId], routeData.permissions);
        }
      }
      if (!hasPermission) {
        await router.navigate(['error/403']);
      }
      return hasPermission;
    } else {
      await router.navigate(['error/403']);
      return false;
    }
  } catch (_error) {
    alertMessageService.showError({ title: 'AccessVerifierGuard Error', error: _error });
    return false;
  }
};
